import { computed, inject } from '@angular/core';
import { Invoice } from '@kinet-open-network/client/invoices/data-access';
import { callApiMethod } from '@kinet-open-network/client/shared/data-access/state';
import { PaymentIntentState } from '@kinet-open-network/shared/data-access/model';
import { errorToString } from '@kinet-open-network/shared/util';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { InvoiceStatus } from '../models/invoice-status.model';
import { PaymentStatus } from '../models/payment-status.model';
import { ZoneInvoicesService } from '../services/zone-invoices.service';
import { ZonePaymentsService } from '../services/zone-payments.service';

export interface ZoneInvoicesState {
  invoiceStatus: InvoiceStatus | null;
  invoiceStatusLoading: boolean;
  invoiceStatusError: string | null;

  paymentStatus: PaymentStatus | null;
  paymentStatusLoading: boolean;
  paymentStatusError: string | null;
  paymentRefreshing: boolean;

  latestInvoices: Invoice[];
  latestInvoicesLoading: boolean;
  latestInvoicesError: string | null;
}

export const ZoneInvoicesStore = signalStore(
  { protectedState: false },
  withState<ZoneInvoicesState>({
    invoiceStatus: null,
    invoiceStatusLoading: false,
    invoiceStatusError: null,
    paymentStatus: null,
    paymentStatusLoading: false,
    paymentStatusError: null,
    paymentRefreshing: false,
    latestInvoices: [],
    latestInvoicesLoading: false,
    latestInvoicesError: null,
  }),
  withComputed((store) => ({
    unpaidTotal: computed(
      () => store.invoiceStatus()?.totalUnliquidatedAmount ?? 0,
    ),
    canLiquidateTotal: computed(
      () => store.invoiceStatus()?.canLiquidateAmount ?? 0,
    ),
    unpaidCount: computed(() => store.invoiceStatus()?.invoices.length),
    paymentState: computed(() => store.paymentStatus()?.state),
    /** payment accepted by the gateway but not booked yet */
    paymentBeingBooked: computed(() => {
      const status = store.paymentStatus();

      return status?.state === PaymentIntentState.succeeded && !status.settled;
    }),
  })),
  withMethods(
    (
      store,
      invoicesService = inject(ZoneInvoicesService),
      paymentsService = inject(ZonePaymentsService),
    ) => ({
      loadStatus: async () => {
        patchState(store, { invoiceStatusLoading: true });
        const [invoiceStatus, paymentStatus] = await Promise.all([
          callApiMethod(() => invoicesService.getInvoiceStatus(), {
            next: (unpaidInvoices) => {
              patchState(store, {
                invoiceStatus: unpaidInvoices,
                invoiceStatusLoading: false,
              });
            },
            error: (err) => {
              patchState(store, {
                invoiceStatusLoading: false,
                invoiceStatusError: errorToString(err),
              });
            },
          }),
          callApiMethod(() => paymentsService.getPaymentStatus(), {
            next: (paymentStatus) => {
              patchState(store, { paymentStatus, paymentStatusLoading: false });
            },
            error: (err) => {
              patchState(store, {
                paymentStatusLoading: false,
                paymentStatusError: errorToString(err),
              });
            },
          }),
        ]);

        return { invoiceStatus, paymentStatus };
      },
      /**
       * Asks the payment provider for the current status of the latest payment
       */
      refreshPayment: async () => {
        const paymentId = store.paymentStatus()?.paymentId;

        if (!paymentId) {
          return;
        }

        patchState(store, { paymentRefreshing: true });

        try {
          const paymentStatus =
            await paymentsService.refreshPaymentStatus(paymentId);

          patchState(store, { paymentStatus });
        } catch (error) {
          // the error is reported by the api client, the stored status stays
          console.error('Error refreshing payment status', error);
        } finally {
          patchState(store, { paymentRefreshing: false });
        }
      },
      loadLatest: async () => {
        patchState(store, { latestInvoicesLoading: true });

        return callApiMethod(() => invoicesService.getLatestInvoices(), {
          next: (invoices) => {
            patchState(store, {
              latestInvoices: invoices,
              latestInvoicesLoading: false,
            });
          },
          error: (error) => {
            patchState(store, {
              latestInvoicesLoading: false,
              latestInvoicesError: errorToString(error),
            });
          },
        });
      },
      downloadInvoice: async (invoice: Invoice) => {
        return callApiMethod(
          () => invoicesService.downloadInvoice(invoice),
          {},
        );
      },
    }),
  ),
);
